import { useEffect, useRef, useState } from 'react';
import {
  Camera,
  ChevronLeft,
  FileText,
  Image as ImageIcon,
  MapPin,
  Menu as MenuIcon,
  Mic,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { tirarFoto, pegarLocalizacao } from '@/modules/agronomia/visita/capture';
import { iniciarGravacao, suportaGravacao, type GravacaoAtiva } from '@/modules/agronomia/visita/audio';
import { COMANDOS, filtrarComandos } from '../commands';
import { escolherArquivo, uploadAnexo } from '../upload';

export interface EnviarPayload {
  texto?: string;
  tipo?: string;
  anexo_url?: string;
  anexo_meta?: unknown;
}

export function ChatInput({
  onEnviar,
  disabled,
}: {
  onEnviar: (p: EnviarPayload) => void;
  disabled?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [menuComandos, setMenuComandos] = useState(false);
  const [menuAnexo, setMenuAnexo] = useState(false);
  const [gravacao, setGravacao] = useState<GravacaoAtiva | null>(null);
  const [gravStart, setGravStart] = useState(0);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const sugestoes = filtrarComandos(texto.trim());
  const mostrarSugestoes = sugestoes.length > 0 && /^\/[a-z]*$/i.test(texto.trim());

  // fecha menus ao clicar fora
  useEffect(() => {
    if (!menuComandos && !menuAnexo) return undefined;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenuComandos(false);
        setMenuAnexo(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuComandos, menuAnexo]);

  function enviarTexto() {
    const t = texto.trim();
    if (!t || disabled) return;
    onEnviar({ texto: t });
    setTexto('');
  }

  function inserirComando(cmd: string) {
    setTexto(cmd + ' ');
    setMenuComandos(false);
  }

  async function comFoto(fromGaleria: boolean) {
    setMenuAnexo(false);
    setBusy(true);
    try {
      const f = fromGaleria ? await escolherArquivo('image/*') : await tirarFoto();
      if (f) {
        const r = await uploadAnexo(f.blob, 'filename' in f ? f.filename : 'foto.jpg');
        onEnviar({ tipo: 'foto', anexo_url: r.anexo_url, anexo_meta: r.anexo_meta });
      }
    } finally {
      setBusy(false);
    }
  }

  async function comArquivo() {
    setMenuAnexo(false);
    setBusy(true);
    try {
      const f = await escolherArquivo();
      if (f) {
        const r = await uploadAnexo(f.blob, f.filename);
        onEnviar({ tipo: r.tipo, anexo_url: r.anexo_url, anexo_meta: r.anexo_meta });
      }
    } finally {
      setBusy(false);
    }
  }

  async function comLocalizacao() {
    setMenuAnexo(false);
    setBusy(true);
    try {
      const c = await pegarLocalizacao();
      if (c) onEnviar({ tipo: 'localizacao', texto: 'Localização', anexo_meta: c });
    } finally {
      setBusy(false);
    }
  }

  async function gravar() {
    if (gravacao) {
      // parar + enviar
      const g = gravacao;
      setGravacao(null);
      setBusy(true);
      try {
        const r = await g.stop();
        if (r) {
          const up = await uploadAnexo(r.blob, r.filename);
          onEnviar({
            tipo: 'audio',
            anexo_url: up.anexo_url,
            anexo_meta: { ...(up.anexo_meta as Record<string, unknown>), dur_ms: r.durMs },
          });
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const g = await iniciarGravacao();
      setGravStart(Date.now());
      setGravacao(g);
    } catch {
      /* permissão negada — ignora */
    }
  }

  function cancelarGravacao() {
    if (!gravacao) return;
    gravacao.cancel();
    setGravacao(null);
  }

  const temTexto = texto.trim().length > 0;

  return (
    <div
      ref={wrapRef}
      className="relative shrink-0 border-t border-border bg-surface px-2.5 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
    >
      {/* autocomplete de comandos (digitando /) */}
      {mostrarSugestoes && !gravacao && (
        <Painel>
          {sugestoes.map((c) => (
            <ItemComando key={c.cmd} cmd={c.cmd} desc={c.desc} onClick={() => inserirComando(c.cmd)} />
          ))}
        </Painel>
      )}

      {/* menu de comandos (botão Menu) */}
      {menuComandos && (
        <Painel>
          {COMANDOS.map((c) => (
            <ItemComando key={c.cmd} cmd={c.cmd} desc={c.desc} onClick={() => inserirComando(c.cmd)} />
          ))}
        </Painel>
      )}

      {/* painel de anexos (paperclip) — grade horizontal, estilo Figma */}
      {menuAnexo && (
        <div className="absolute bottom-[calc(100%+6px)] left-2.5 right-2.5 z-20 rounded-2xl border border-border bg-surface px-3 py-4 shadow-[var(--shadow-over)]">
          <div className="flex items-start justify-around">
            <AnexoBtn icon={ImageIcon} label="Galeria" onClick={() => void comFoto(true)} disabled={busy} />
            <AnexoBtn icon={Camera} label="Câmera" onClick={() => void comFoto(false)} disabled={busy} />
            <AnexoBtn icon={FileText} label="Arquivo" onClick={() => void comArquivo()} disabled={busy} />
            <AnexoBtn icon={MapPin} label="Localização" onClick={() => void comLocalizacao()} disabled={busy} />
          </div>
        </div>
      )}

      <div className="flex items-end gap-1.5">
        {/* botão Menu (comandos) — pílula coral, estilo Figma */}
        <button
          type="button"
          onClick={() => {
            setMenuComandos((v) => !v);
            setMenuAnexo(false);
          }}
          className={cn(
            'flex items-center gap-1.5 rounded-full px-3 h-9 shrink-0 text-[0.8rem] font-semibold',
            'bg-[var(--accent)] text-[var(--on-solid)] hover:bg-[var(--accent2)] transition-colors',
          )}
          aria-label="Comandos"
        >
          {menuComandos ? <X size={17} strokeWidth={2.25} /> : <MenuIcon size={17} strokeWidth={2.25} />}
          Menu
        </button>

        {gravacao ? (
          <RecordingBar startMs={gravStart} onCancel={cancelarGravacao} />
        ) : (
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviarTexto();
              }
            }}
            rows={1}
            placeholder="Mensagem…"
            disabled={busy}
            className={cn(
              'flex-1 resize-none max-h-32 rounded-2xl px-3.5 py-2',
              'bg-surface2 border border-border text-[0.9rem] text-text',
              'placeholder:text-text-muted focus:outline-none focus:border-border2',
            )}
          />
        )}

        {/* paperclip (anexos) — escondido enquanto grava */}
        {!gravacao && (
          <BotaoIcone
            onClick={() => {
              setMenuAnexo((v) => !v);
              setMenuComandos(false);
            }}
            ativo={menuAnexo}
            disabled={busy}
            aria-label="Anexar"
          >
            <Paperclip size={19} strokeWidth={1.75} />
          </BotaoIcone>
        )}

        {/* mic (vazio) / enviar (com texto) / parar (gravando) */}
        {temTexto && !gravacao ? (
          <BotaoEnviar onClick={enviarTexto} disabled={disabled || busy}>
            <Send size={17} strokeWidth={2} />
          </BotaoEnviar>
        ) : (
          <BotaoEnviar
            onClick={() => void gravar()}
            disabled={busy || (!gravacao && !suportaGravacao())}
          >
            <Mic size={18} strokeWidth={1.75} />
          </BotaoEnviar>
        )}
      </div>
    </div>
  );
}

function Painel({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute bottom-[calc(100%+6px)] left-2.5 right-2.5 z-20 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface shadow-[var(--shadow-over)] py-1">
      {children}
    </div>
  );
}

function ItemComando({ cmd, desc, onClick }: { cmd: string; desc: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-baseline gap-3 w-full text-left px-3 py-1.5 hover:bg-surface2"
    >
      <span className="text-[0.85rem] font-medium text-accent shrink-0">{cmd}</span>
      <span className="text-[0.8rem] text-text-2 truncate">{desc}</span>
    </button>
  );
}

function AnexoBtn({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof Camera;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-16 flex-col items-center gap-1.5 disabled:opacity-40"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-surface2">
        <Icon size={22} strokeWidth={1.5} className="text-text" />
      </span>
      <span className="text-[0.72rem] text-text-2">{label}</span>
    </button>
  );
}

// Barra de gravação: ● timer + "‹ deslize para cancelar". Arrastar a barra pra
// esquerda e soltar cancela (estilo WhatsApp); tocar no mic envia.
function RecordingBar({ startMs, onCancel }: { startMs: number; onCancel: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  const [cancelando, setCancelando] = useState(false);
  const startXRef = useRef<number | null>(null);
  useEffect(() => {
    const t = window.setInterval(() => setElapsed(Date.now() - startMs), 250);
    return () => window.clearInterval(t);
  }, [startMs]);
  const mm = Math.floor(elapsed / 60000);
  const ss = Math.floor((elapsed % 60000) / 1000);
  return (
    <div
      className="flex h-9 flex-1 touch-none select-none items-center gap-2 rounded-2xl bg-surface2 px-3"
      onPointerDown={(e) => {
        startXRef.current = e.clientX;
      }}
      onPointerMove={(e) => {
        if (startXRef.current != null) setCancelando(e.clientX - startXRef.current < -56);
      }}
      onPointerUp={() => {
        if (cancelando) onCancel();
        startXRef.current = null;
        setCancelando(false);
      }}
      onPointerLeave={() => {
        startXRef.current = null;
        setCancelando(false);
      }}
    >
      <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-danger" />
      <span className="shrink-0 text-[0.85rem] tabular-nums text-text">
        {mm}:{String(ss).padStart(2, '0')}
      </span>
      <span
        className={cn(
          'flex flex-1 items-center justify-center gap-1 text-[0.8rem]',
          cancelando ? 'font-medium text-danger' : 'text-text-2',
        )}
      >
        <ChevronLeft size={14} strokeWidth={2} />
        {cancelando ? 'solte para cancelar' : 'deslize para cancelar'}
      </span>
    </div>
  );
}

function BotaoIcone({
  children,
  onClick,
  ativo,
  disabled,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  ativo?: boolean;
  disabled?: boolean;
  'aria-label': string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center size-9 rounded-full shrink-0 transition-colors',
        ativo ? 'bg-surface2 text-accent' : 'text-text-2 hover:bg-surface2',
        'disabled:opacity-40',
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

function BotaoEnviar({
  children,
  onClick,
  disabled,
  tom = 'accent',
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tom?: 'accent' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center size-9 rounded-full shrink-0 transition-opacity',
        tom === 'danger' ? 'bg-danger' : 'bg-[var(--accent)]',
        'text-[var(--on-solid)] disabled:opacity-40',
      )}
      aria-label="Enviar"
    >
      {children}
    </button>
  );
}
